// A "report section" is one numbered finding the agent writes for the
// detailed-report body — e.g. "1. Social Media Review" + a paragraph beneath.
// We store the canonical representation as plain text in `reportDetails` so
// the backend stays simple; this module handles parse/serialize between that
// text blob and the structured array the UI works in.

export interface ReportSection {
  heading: string;
  body: string;
}

// Matches a line that opens a new numbered section:  "1. Title", "12.  Title"
const HEADING_RE = /^\s*(\d+)\.\s+(.+?)\s*$/;

export function parseReportSections(raw: string | null | undefined): ReportSection[] {
  if (!raw || !raw.trim()) return [];

  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const sections: ReportSection[] = [];
  let current: ReportSection | null = null;
  // Buffer for content that appears before the first numbered heading — we
  // keep it so an unstructured legacy report doesn't silently disappear.
  let preamble: string[] = [];

  for (const line of lines) {
    const match = line.match(HEADING_RE);
    if (match && match[2]) {
      if (current) sections.push(current);
      current = { heading: match[2].trim(), body: '' };
    } else if (current) {
      current.body += (current.body ? '\n' : '') + line;
    } else {
      preamble.push(line);
    }
  }
  if (current) sections.push(current);

  // Trim trailing whitespace inside each body but preserve internal paragraph
  // breaks.
  for (const s of sections) {
    s.body = s.body.replace(/\s+$/g, '');
  }

  // If the raw text wasn't numbered at all, fall back to a single section so
  // the agent can still edit legacy reports.
  if (sections.length === 0 && preamble.some((l) => l.trim().length > 0)) {
    return [{ heading: '', body: preamble.join('\n').trim() }];
  }

  // Stash any preamble that appeared *before* the first heading as a leading
  // unnamed section so we don't lose content during edit/save round-trips.
  if (preamble.some((l) => l.trim().length > 0)) {
    sections.unshift({ heading: '', body: preamble.join('\n').trim() });
  }

  return sections;
}

// Serialize sections back into the canonical text format. Empty headings are
// dropped (their body still appears as a leading block).
export function serializeReportSections(sections: ReportSection[]): string {
  const cleaned = sections
    .map((s) => ({ heading: s.heading.trim(), body: s.body.trim() }))
    .filter((s) => s.heading || s.body);

  let n = 1;
  const blocks = cleaned.map((s) => {
    if (!s.heading) return s.body;
    const header = `${n}. ${s.heading}`;
    n += 1;
    return s.body ? `${header}\n${s.body}` : header;
  });

  return blocks.join('\n\n');
}

// Lightweight validator for the submit-report flow.
export interface ReportSectionsError {
  index: number;
  message: string;
}

export function validateReportSections(sections: ReportSection[]): ReportSectionsError | null {
  const populated = sections.filter((s) => s.heading.trim() || s.body.trim());
  if (populated.length === 0) {
    return { index: 0, message: 'Add at least one section to the detailed report.' };
  }
  for (let i = 0; i < sections.length; i += 1) {
    const s = sections[i];
    if (!s) continue;
    const hasHeading = s.heading.trim().length > 0;
    const hasBody = s.body.trim().length > 0;
    if (hasHeading && !hasBody) {
      return { index: i, message: 'Each section needs a body — fill in or remove this one.' };
    }
    if (!hasHeading && hasBody && i > 0) {
      return {
        index: i,
        message: 'Add a heading for this section, or remove it.',
      };
    }
  }
  return null;
}
